// #82 Sync: the integration strip under an event header, 40 px on --card-2 with a 3 px rule, in three states —
// *synchronizing* (blue rule, aria-busy: the L state), *delivered* (green rule, the time it landed) and the failure
// form, which is F4a's ErrorStrip (the red rule, the error line, **Retry** and *Error details* with Correlation ID ·
// status · retries · error details · last attempt: "Failed sync is visible and retryable."). *Manual recovery* sits
// behind the matrix *Override*: drawn only for a seat that holds it (`canOverride`), absent otherwise (DC-24).

import type { ReactNode } from "react";
import { Button, type Blocked } from "../core/Button";
import { ErrorStrip, type ErrorDetails } from "../core/ErrorStrip";
import { Icon } from "../core/icons";
import { CORE_LABELS } from "../core/labels";
import { Pill } from "../core/Pill";
import { cx } from "../lib/cx";
import { DEFAULT_TIME_ZONE, formatDateTime, toEpoch, type Instant } from "../lib/time";
import { SYNC_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export type SyncStatus = "synchronizing" | "delivered" | "failure";

export interface SyncProps {
  status: SyncStatus;
  /** The strip's name (default *Delivery status*). */
  label?: string | undefined;
  /** delivered: when it landed. */
  at?: Instant | undefined;
  timeZone?: string | undefined;
  /** failure: the one-line error (default *Sync failure*). */
  message?: ReactNode | undefined;
  details?: ErrorDetails | undefined;
  onRetry?: (() => void) | undefined;
  retrying?: boolean | undefined;
  canRetry?: boolean | undefined;
  retryBlocked?: Blocked | undefined;
  /** The matrix Override: Manual recovery is drawn only when true. */
  canOverride?: boolean | undefined;
  onRecover?: (() => void) | undefined;
  recoverBlocked?: Blocked | undefined;
  defaultOpen?: boolean | undefined;
  /** The failure appeared while the person was here (role="alert"). */
  live?: boolean | undefined;
  hs?: Hs | undefined;
  recoverHs?: Hs | undefined;
  className?: string | undefined;
}

export function Sync({
  status, label = SYNC_LABELS.deliveryStatus, at, timeZone = DEFAULT_TIME_ZONE, message, details, onRetry, retrying, canRetry,
  retryBlocked, canOverride, onRecover, recoverBlocked, defaultOpen, live, hs, recoverHs, className,
}: SyncProps) {
  if (status === "failure") {
    return (
      <ErrorStrip
        className={cx("mc-sync", "mc-sync-failure", className)}
        message={message ?? CORE_LABELS.syncFailure}
        onRetry={onRetry}
        retrying={retrying}
        canRetry={canRetry}
        retryBlocked={retryBlocked}
        details={details}
        defaultOpen={defaultOpen}
        live={live}
        hs={hs}
        actions={(
          <Button sm tone="ghost" icon="shield" can={canOverride === true} blocked={recoverBlocked} onClick={onRecover} hs={recoverHs}>
            {SYNC_LABELS.manualRecovery}
          </Button>
        )}
      />
    );
  }
  const busy = status === "synchronizing";
  return (
    <div className={cx("mc-sync", `mc-sync-${status}`, className)} role="status" aria-busy={busy ? true : undefined}>
      <Icon name={busy ? "refresh" : "check"} size={16} />
      <span className="mc-sync-k">{label}</span>
      <Pill tone={busy ? "info" : "ok"}>{busy ? SYNC_LABELS.synchronizing : SYNC_LABELS.delivered}</Pill>
      {!busy && at !== undefined ? (
        <time className="mc-sync-at" dateTime={new Date(toEpoch(at)).toISOString()}>{formatDateTime(at, timeZone)}</time>
      ) : null}
    </div>
  );
}
